import type { StateCreator } from "zustand";
import type { DBOpsState } from "../useStudioAPI/hooks/dbOps.store";
import type {
  ProcessProps,
  Swimlane,
  Step,
  Field,
  Rule,
  BulkAction,
  RuleData,
  Change,
  Process,
  MegaJSON
} from "@certa/studio-core";
import {
  ComponentKey,
  ComponentTypeToRuleTypeMap
} from "@certa/studio-core/src/constants";
import type {
  ConditionGroupData,
  ConditionData
} from "../../components/RulesUI/Constraints/constraints.types";
import type {
  EventRule,
  StateRule
} from "@certa/studio-core/src/types/eca.types";
import type {
  FieldLookupData,
  ProcessLookUpData,
  StepLookupData,
  SwimlaneLookupData
} from "../processLookup/processLookup.types";
import type { ZodRawProcess } from "@certa/queries/schemas/studio/deployableJSON.schema";
import { mutate } from "@certa/studio-core/src/state/stateMutators";
import {
  insertTimeStampInValue,
  insertTimeStampsInSetter,
  insertTimeStampsInBulkSetter
} from "../../utils/dbOps.utils";
import { createRemoveRulesChanges } from "@certa/studio-core/src/utils";
import { migrations } from "../../constants/migration.constants";

export const initialMegaJSON: MegaJSON = {
  id: null,
  processes: {},
  app_settings: {
    group: [],
    emailtemplate: [],
    emailattachment: [],
    businessunit: [],
    region: [],
    extrajson: [],
    workflowkind: [],
    workflowstatus: [],
    objectstatus: [],
    workflowpdftemplate: [],
    alertcategory: [],
    commentflagoption: [],
    objecttypeworkflowkindrelation: [],
    integrationconfig: [],
    integrationtoken: []
  },
  base_app_settings: {
    emailtemplate: [],
    emailattachment: [],
    businessunit: [],
    region: [],
    extrajson: [],
    workflowkind: [],
    workflowstatus: [],
    objectstatus: [],
    workflowpdftemplate: [],
    alertcategory: [],
    commentflagoption: [],
    objecttypeworkflowkindrelation: [],
    group: [],
    integrationconfig: [],
    integrationtoken: []
  },
  records: {},
  __appConfigSaveId: "",
  __saveId: ""
};

export const emptyProcess: Process = {
  process: {
    id: "",
    kind: {
      name: "",
      tag: "",
      defaultStatus: "",
      availableStatuses: [],
      permissions: {
        dynamicGroups: {},
        staticGroups: {}
      }
    },
    swimlaneSequence: [],
    eventRuleSequence: [],
    stateRuleSequence: [],
    __createdAt: 0,
    __modifiedAt: 0,
    __branchTimestamp: 0,
    __saveId: "",
    __lastMigration: migrations[migrations.length - 1].id
  },
  eventRules: {},
  stateRules: {},
  rules: {},
  fields: {},
  steps: {},
  swimlanes: {},
  conditionGroups: {},
  conditions: {},
  bulkActions: {}
};

export type ProcessSlice = {
  megaJSON: MegaJSON;
  getMegaJSONDef: () => MegaJSON;
  updateMegaJSON: (megaJSON: MegaJSON) => void;
  getProcessDef: (processId: string) => Process;
  getProcesses: () => Process[];
  getProcess: (processId: string) => ProcessProps | undefined;
  getProcessByTag: (processTag: string) => ProcessProps | undefined;
  getSwimlane: (processId: string, swimlaneId: string) => Swimlane;
  getSwimlanes: (processId: string, swimlaneIds: string[]) => Swimlane[];
  getSwimlaneByTag: (
    processId: string,
    swimlaneTag: string
  ) => Swimlane | undefined;
  getSwimlaneByStepId: (
    processId: string,
    stepId: string
  ) => Swimlane | undefined;
  getStep: (processId: string, stepId: string) => Step;
  getSteps: (processId: string, stepIds: string[]) => Step[];
  getStepByTag: (processId: string, stepTag: string) => Step | undefined;
  getStepByFieldId: (processId: string, fieldId: string) => Step | undefined;
  getField: (processId: string, fieldId: string) => Field;
  getFields: (processId: string, fieldIds: string[]) => Field[];
  getFieldByTag: (processId: string, fieldTag: string) => Field | undefined;
  getRule: (processId: string, ruleId: string) => Rule;
  getRules: (processId: string, ruleIds: string[]) => Rule[];
  getConditionGroup: (
    processId: string,
    conditionGroupId: string
  ) => ConditionGroupData;
  getConditionGroups: (
    processId: string,
    conditionGroupIds: string[]
  ) => ConditionGroupData[];
  getCondition: (processId: string, conditionId: string) => ConditionData;
  getConditions: (processId: string, conditionIds: string[]) => ConditionData[];
  getBulkAction: (processId: string, bulkActionId: string) => BulkAction;
  getBulkActions: (processId: string, bulkActionIds: string[]) => BulkAction[];
  getComponentByTag: (props: {
    processId: string;
    componentKey:
      | ComponentKey.PROCESS
      | ComponentKey.SWIMLANES
      | ComponentKey.STEPS
      | ComponentKey.FIELDS;
    componentTag?: string;
  }) => ProcessProps | Swimlane | Step | Field | undefined;
  getComponent: (
    processId: string,
    componentKey:
      | ComponentKey.PROCESS
      | ComponentKey.SWIMLANES
      | ComponentKey.STEPS
      | ComponentKey.FIELDS,
    componentId: string
  ) => ProcessProps | Swimlane | Step | Field | undefined;
  getMetaJSONLookUpComponent: (
    processId: string,
    componentId: string,
    componentKey: ComponentKey
  ) =>
    | FieldLookupData
    | StepLookupData
    | SwimlaneLookupData
    | ProcessLookUpData
    | undefined;
  getConditionsOfProcess: (processId: string) => ConditionData[];
  getConditionGroupsOfProcess: (processId: string) => ConditionGroupData[];
  getFieldsOfProcess: (processId: string) => Field[];
  getStepsOfProcess: (processId: string) => Step[];
  getSwimlanesOfProcess: (processId: string) => Swimlane[];
  getOnCreateWorkflow: (
    processId: string
  ) => ZodRawProcess["on_create_workflow"];
  getEventRule: (
    processId: string,
    eventRuleId: string
  ) => EventRule | undefined;
  getEventRules: (processId: string) => Record<string, EventRule>;
  getStateRule: (
    processId: string,
    stateRuleId: string
  ) => StateRule | undefined;
  getStateRules: (processId: string) => Record<string, StateRule>;

  // Path-based getter
  getProcessDataByPath: (processId: string, path: string) => any;

  // Add
  addField: (processId: string, field: Field) => void;
  addStep: (processId: string, step: Step) => void;
  addSwimlane: (processId: string, swimlane: Swimlane) => void;
  addRule: (processId: string, rule: Rule) => void;
  addEventRule: (processId: string, eventRule: EventRule) => void;
  addStateRule: (processId: string, stateRule: StateRule) => void;

  // Setters
  setProcess: (
    processId: string,
    process: ProcessProps,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  replaceProcess: (processId: string, process: Process) => void;
  setSwimlane: (
    processId: string,
    swimlane: Swimlane,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setStep: (
    processId: string,
    step: Step,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setField: (
    processId: string,
    field: Field,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setRule: (
    processId: string,
    rule: RuleData,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setConditionGroup: (
    processId: string,
    conditionGroup: ConditionGroupData,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setCondition: (
    processId: string,
    condition: ConditionData,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setBulkAction: (
    processId: string,
    bulkAction: BulkAction,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;

  // ECA
  setEventRule: (processId: string, eventRule: EventRule) => void;
  setStateRule: (processId: string, stateRule: StateRule) => void;

  // Bulk setters
  bulkSetFields: (
    processId: string,
    fields: Field[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetSteps: (
    processId: string,
    steps: Step[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetSwimlanes: (
    processId: string,
    swimlanes: Swimlane[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetRules: (
    processId: string,
    rules: Rule[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetConditionGroups: (
    processId: string,
    conditionGroups: ConditionGroupData[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetConditions: (
    processId: string,
    conditions: ConditionData[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetBulkActions: (
    processId: string,
    bulkActions: BulkAction[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;

  // Updates
  updateProcess: (
    processId: string,
    process: Partial<ProcessProps>,
    config?: {
      shouldSkipUpdatingModifiedAtTimestamp?: boolean;
      shouldSkipChange?: boolean;
    }
  ) => void;
  updateField: (
    processId: string,
    fieldId: string,
    fieldProps: Partial<Field>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateStep: (
    processId: string,
    stepId: string,
    stepProps: Partial<Step>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateSwimlane: (
    processId: string,
    swimlaneId: string,
    swimlaneProps: Partial<Swimlane>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateRule: (
    processId: string,
    ruleId: string,
    ruleProps: Partial<Rule>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateConditionGroup: (
    processId: string,
    conditionGroupId: string,
    props: Partial<ConditionGroupData>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateCondition: (
    processId: string,
    conditionId: string,
    props: Partial<ConditionData>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateBulkAction: (
    processId: string,
    bulkActionId: string,
    props: Partial<BulkAction>,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateEventRule: (
    processId: string,
    eventRule: EventRule,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  updateStateRule: (
    processId: string,
    stateRule: StateRule,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  replaceAllEventRules: (
    processId: string,
    eventRules: Process["eventRules"],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  replaceAllStateRules: (
    processId: string,
    stateRules: Process["stateRules"],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;

  // Remove functions
  removeField: (processId: string, fieldId: string) => void;
  removeFields: (processId: string, fieldIds: string[]) => void;
  removeStep: (processId: string, stepId: string) => void;
  removeSteps: (processId: string, stepIds: string[]) => void;
  removeSwimlane: (processId: string, swimlaneId: string) => void;
  removeSwimlanes: (processId: string, swimlaneIds: string[]) => void;
  removeRule: (processId: string, ruleId: string) => void;
  removeRules: (processId: string, ruleIds: string[]) => void;
  deleteProcess: (processId: string) => void;

  // ECA
  removeEventRule: (processId: string, eventRuleId: string) => void;
  removeStateRule: (processId: string, stateRuleId: string) => void;

  // Delete (private)
  __deleteSwimlane: (processId: string, swimlaneId: string) => void;
  __deleteStep: (processId: string, stepId: string) => void;
  __deleteField: (processId: string, fieldId: string) => void;
};

const getProcessFromMegaJSON = (megaJSON: MegaJSON, processId: string) => {
  const process = megaJSON.processes[processId];
  if (!process) return emptyProcess;
  return structuredClone(process);
};

export const processSlice: StateCreator<DBOpsState, [], [], ProcessSlice> = (
  set,
  get
) => ({
  megaJSON: initialMegaJSON,

  getMegaJSONDef: () => get().megaJSON,

  getProcessDef: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process);
  },

  getProcess: processId => {
    const { megaJSON } = get();
    // getProcessFromMegaJSON is returning empty process if process is not found in megaJSON,
    // we need to return undefined instead of empty process
    // TODO: Fix this in getProcessFromMegaJSON and remove this check
    const process = getProcessFromMegaJSON(megaJSON, processId);
    const processProps = structuredClone(process.process);
    return processProps.id ? processProps : undefined;
  },

  updateMegaJSON: megaJSON => {
    get().applyChanges([
      {
        path: "",
        to: megaJSON,
        from: get().megaJSON
      }
    ]);
  },

  getProcesses: () => {
    const { megaJSON } = get();
    return structuredClone(Object.values(megaJSON.processes));
  },

  getProcessByTag: processTag => {
    const { megaJSON } = get();
    const process =
      Object.values(megaJSON.processes).find(
        p => p.process.kind.tag === processTag
      ) || emptyProcess;
    return structuredClone(process?.process);
  },

  // Swimlane-related getters
  getSwimlane: (processId, swimlaneId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.swimlanes[swimlaneId]);
  },

  getSwimlanes: (processId, swimlaneIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      swimlaneIds.map(swimlaneId => process.swimlanes[swimlaneId])
    );
  },

  getSwimlaneByTag: (processId, swimlaneTag) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      Object.values(process.swimlanes).find(s => s.tag === swimlaneTag)
    );
  },

  getSwimlaneByStepId: (processId, stepId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      Object.values(process.swimlanes).find(s => s.steps.includes(stepId))
    );
  },

  // Step-related getters
  getStep: (processId, stepId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.steps[stepId]);
  },

  getSteps: (processId, stepIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(stepIds.map(stepId => process.steps[stepId]));
  },

  getStepByTag: (processId, stepTag) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      Object.values(process.steps).find(s => s.tag === stepTag)
    );
  },

  getStepByFieldId: (processId, fieldId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      Object.values(process.steps).find(s => s.fields.includes(fieldId))
    );
  },

  // Field-related getters
  getField: (processId, fieldId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.fields[fieldId]);
  },

  getFields: (processId, fieldIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(fieldIds.map(fieldId => process.fields[fieldId]));
  },

  getFieldByTag: (processId, fieldTag) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      Object.values(process.fields).find(f => f.tag === fieldTag)
    );
  },

  // Rule-related getters
  getRule: (processId, ruleId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.rules[ruleId]);
  },

  getRules: (processId, ruleIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(ruleIds.map(ruleId => process.rules[ruleId]));
  },

  // Condition-related getters
  getCondition: (processId, conditionId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.conditions[conditionId]);
  },

  getConditions: (processId, conditionIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      conditionIds.map(conditionId => process.conditions[conditionId])
    );
  },

  getConditionGroup: (processId, conditionGroupId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.conditionGroups[conditionGroupId]);
  },

  getConditionGroups: (processId, conditionGroupIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      conditionGroupIds.map(
        conditionGroupId => process.conditionGroups[conditionGroupId]
      )
    );
  },

  getBulkAction: (processId, bulkActionId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.bulkActions[bulkActionId]);
  },

  getBulkActions: (processId, bulkActionIds) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(
      bulkActionIds.map(bulkActionId => process.bulkActions[bulkActionId])
    );
  },

  getComponentByTag: ({ processId, componentKey, componentTag }) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    if (componentKey === ComponentKey.PROCESS) {
      return structuredClone(process[componentKey]);
    }

    const components = Object.values(process[componentKey]) as (
      | Swimlane
      | Step
      | Field
    )[];

    return structuredClone(components.find(cmp => cmp.tag === componentTag));
  },

  getComponent: (processId, componentKey, componentId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    if (componentKey === ComponentKey.PROCESS) {
      return structuredClone(process[componentKey]);
    }
    return structuredClone(process[componentKey]?.[componentId]);
  },

  getMetaJSONLookUpComponent: (processId, componentId, componentKey) => {
    const megaJSONLookUpData = get().megaJSONLookUpData;
    const metaJSONLookUpData = megaJSONLookUpData[processId] || {
      metaJSONLookup: {}
    };
    const {
      metaJSONLookup: { fields = {}, steps = {}, swimlanes = {}, process }
    } = metaJSONLookUpData;

    if (!componentId || !componentKey) return;

    switch (componentKey) {
      case ComponentKey.FIELDS:
        return structuredClone(fields[componentId]);
      case ComponentKey.STEPS:
        return structuredClone(steps[componentId]);
      case ComponentKey.SWIMLANES:
        return structuredClone(swimlanes[componentId]);
      case ComponentKey.PROCESS:
        return structuredClone(process[componentId]);
      default:
        return undefined;
    }
  },

  // Process-wide getters
  getConditionsOfProcess: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(Object.values(process.conditions));
  },

  getFieldsOfProcess: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(Object.values(process.fields));
  },

  getConditionGroupsOfProcess: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(Object.values(process.conditionGroups));
  },

  getStepsOfProcess: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(Object.values(process.steps));
  },

  getSwimlanesOfProcess: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(Object.values(process.swimlanes));
  },

  // ECA-related getters
  getEventRule: (processId, eventRuleId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.eventRules?.[eventRuleId]);
  },
  getEventRules: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.eventRules || {});
  },

  getStateRule: (processId, stateRuleId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.stateRules?.[stateRuleId]);
  },
  getStateRules: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return structuredClone(process.stateRules || {});
  },

  getOnCreateWorkflow: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    return process.process.onCreateWorkflow;
  },

  // Path-based getter
  getProcessDataByPath: (processId: string, path: string) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);

    // returns whole process if no path passed
    if (!path) {
      return structuredClone(process);
    }

    // Handle path-based access
    const pathParts = path.split(".");
    let dataAtPath: any = process;

    for (const part of pathParts) {
      if (dataAtPath && typeof dataAtPath === "object" && part in dataAtPath) {
        dataAtPath = dataAtPath[part];
      } else {
        dataAtPath = undefined;
        break;
      }
    }

    return structuredClone(dataAtPath);
  },

  // Add Operations
  addField: insertTimeStampsInSetter((processId: string, field: Field) => {
    const changes = [
      mutate.add({
        processId,
        component: field,
        componentKey: ComponentKey.FIELDS
      })
    ];
    get().applyChanges(changes);
  }),

  addStep: insertTimeStampsInSetter((processId: string, step: Step) => {
    const changes = [
      mutate.add({
        processId,
        component: step,
        componentKey: ComponentKey.STEPS
      })
    ];
    get().applyChanges(changes);
  }),

  addSwimlane: insertTimeStampsInSetter(
    (processId: string, swimlane: Swimlane) => {
      const changes = [
        mutate.add({
          processId,
          component: swimlane,
          componentKey: ComponentKey.SWIMLANES
        })
      ];
      get().applyChanges(changes);
    }
  ),

  addRule: insertTimeStampsInSetter((processId: string, rule: Rule) => {
    const changes = [
      mutate.add({
        processId,
        component: rule,
        componentKey: ComponentKey.RULES
      })
    ];
    get().applyChanges(changes);
  }),

  addEventRule: insertTimeStampsInSetter(
    (processId: string, eventRule: EventRule) => {
      const changes = [
        mutate.add({
          processId,
          component: eventRule,
          componentKey: ComponentKey.EVENT_RULES
        })
      ];
      get().applyChanges(changes);
    }
  ),

  addStateRule: insertTimeStampsInSetter(
    (processId: string, stateRule: StateRule) => {
      const changes = [
        mutate.add({
          processId,
          component: stateRule,
          componentKey: ComponentKey.STATE_RULES
        })
      ];
      get().applyChanges(changes);
    }
  ),

  // Individual Setters
  setProcess: insertTimeStampsInSetter(
    (processId: string, process: ProcessProps) => {
      const changes = [
        mutate.updateProcess({
          processId,
          state: get().megaJSON,
          process
        })
      ];
      get().applyChanges(changes);
    }
  ),
  replaceProcess: insertTimeStampsInSetter(
    (processId: string, process: Process) => {
      const changes = [
        mutate.replaceProcess({
          state: get().megaJSON,
          processId,
          process
        })
      ];
      get().applyChanges(changes);
    }
  ),

  setSwimlane: insertTimeStampsInSetter(
    (processId: string, swimlane: Swimlane) => {
      const changes = [
        mutate.update({
          processId,
          state: get().megaJSON,
          component: swimlane,
          componentKey: ComponentKey.SWIMLANES
        })
      ];
      get().applyChanges(changes);
    }
  ),

  setStep: insertTimeStampsInSetter((processId: string, step: Step) => {
    const changes = [
      mutate.update({
        processId,
        state: get().megaJSON,
        component: step,
        componentKey: ComponentKey.STEPS
      })
    ];
    get().applyChanges(changes);
  }),

  setField: insertTimeStampsInSetter((processId: string, field: Field) => {
    const changes = [
      mutate.update({
        processId,
        state: get().megaJSON,
        component: field,
        componentKey: ComponentKey.FIELDS
      })
    ];
    get().applyChanges(changes);
  }),

  setRule: insertTimeStampsInSetter((processId: string, rule: RuleData) => {
    const changes = [
      mutate.update({
        processId,
        state: get().megaJSON,
        component: rule,
        componentKey: ComponentKey.RULES
      })
    ];
    get().applyChanges(changes);
  }),

  setConditionGroup: insertTimeStampsInSetter(
    (processId: string, conditionGroup: ConditionGroupData) => {
      const changes = [
        mutate.update({
          processId,
          state: get().megaJSON,
          component: conditionGroup,
          componentKey: ComponentKey.CONDITION_GROUPS
        })
      ];
      get().applyChanges(changes);
    }
  ),

  setCondition: insertTimeStampsInSetter(
    (processId: string, condition: ConditionData) => {
      const changes = [
        mutate.update({
          processId,
          state: get().megaJSON,
          component: condition,
          componentKey: ComponentKey.CONDITIONS
        })
      ];
      get().applyChanges(changes);
    }
  ),

  setBulkAction: insertTimeStampsInSetter(
    (processId: string, bulkAction: BulkAction) => {
      const changes = [
        mutate.update({
          processId,
          state: get().megaJSON,
          component: bulkAction,
          componentKey: ComponentKey.BULK_ACTIONS
        })
      ];
      get().applyChanges(changes);
    }
  ),

  setEventRule: insertTimeStampsInSetter(
    (processId: string, eventRule: EventRule) => {
      const changes = [
        mutate.update({
          processId,
          state: get().megaJSON,
          component: eventRule,
          componentKey: ComponentKey.EVENT_RULES
        })
      ];
      get().applyChanges(changes);
    }
  ),

  setStateRule: insertTimeStampsInSetter(
    (processId: string, stateRule: StateRule) => {
      const changes = [
        mutate.update({
          processId,
          state: get().megaJSON,
          component: stateRule,
          componentKey: ComponentKey.STATE_RULES
        })
      ];
      get().applyChanges(changes);
    }
  ),

  // Bulk Setters
  bulkSetFields: insertTimeStampsInBulkSetter(
    (processId: string, fields: Field[]) => {
      const changes = fields.map(field =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: field,
          componentKey: ComponentKey.FIELDS
        })
      );
      get().applyChanges(changes);
    }
  ),

  bulkSetSteps: insertTimeStampsInBulkSetter(
    (processId: string, steps: Step[]) => {
      const changes = steps.map(step =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: step,
          componentKey: ComponentKey.STEPS
        })
      );
      get().applyChanges(changes);
    }
  ),

  bulkSetSwimlanes: insertTimeStampsInBulkSetter(
    (processId: string, swimlanes: Swimlane[]) => {
      const changes = swimlanes.map(swimlane =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: swimlane,
          componentKey: ComponentKey.SWIMLANES
        })
      );
      get().applyChanges(changes);
    }
  ),

  bulkSetRules: insertTimeStampsInBulkSetter(
    (processId: string, rules: Rule[]) => {
      const changes = rules.map(rule =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: rule,
          componentKey: ComponentKey.RULES
        })
      );
      get().applyChanges(changes);
    }
  ),

  bulkSetConditionGroups: insertTimeStampsInBulkSetter(
    (processId: string, conditionGroups: ConditionGroupData[]) => {
      const changes = conditionGroups.map(conditionGroup =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: conditionGroup,
          componentKey: ComponentKey.CONDITION_GROUPS
        })
      );
      get().applyChanges(changes);
    }
  ),

  bulkSetConditions: insertTimeStampsInBulkSetter(
    (processId: string, conditions: ConditionData[]) => {
      const changes = conditions.map(condition =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: condition,
          componentKey: ComponentKey.CONDITIONS
        })
      );
      get().applyChanges(changes);
    }
  ),

  bulkSetBulkActions: insertTimeStampsInBulkSetter(
    (processId: string, bulkActions: BulkAction[]) => {
      const changes = bulkActions.map(bulkAction =>
        mutate.update({
          processId,
          state: get().megaJSON,
          component: bulkAction,
          componentKey: ComponentKey.BULK_ACTIONS
        })
      );
      get().applyChanges(changes);
    }
  ),

  updateProcess: (processId, processProps, config = {}) => {
    const { shouldSkipUpdatingModifiedAtTimestamp = false, shouldSkipChange } =
      config;
    const { megaJSON, applyChanges } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);

    if (!process) return;

    const changes = [
      mutate.updateProcess({
        state: megaJSON,
        processId,
        // Updating modifiedAt manually here, and not wrapping with insertTimeStampsInSetter decorator
        // since this setter is not compatible with the decorator
        process: shouldSkipUpdatingModifiedAtTimestamp
          ? {
              ...process.process,
              ...processProps
            }
          : insertTimeStampInValue({
              ...process.process,
              ...processProps
            })
      })
    ];

    if (!shouldSkipChange) {
      applyChanges(changes);
    }
  },

  updateField: insertTimeStampsInSetter(
    (processId: string, fieldId: string, fieldProps: Partial<Field>) => {
      const { megaJSON } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const field = process.fields[fieldId];
      if (!field) return;

      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: { ...field, ...fieldProps },
          componentKey: ComponentKey.FIELDS
        })
      ];
      get().applyChanges(changes);
    },
    true
  ),

  updateStep: insertTimeStampsInSetter(
    (processId: string, stepId: string, stepProps: Partial<Step>) => {
      const { megaJSON } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const step = process.steps[stepId];
      if (!step) return;

      const changes = [
        mutate.update({
          processId,
          state: megaJSON,
          component: { ...step, ...stepProps },
          componentKey: ComponentKey.STEPS
        })
      ];
      get().applyChanges(changes);
    },
    true
  ),

  updateSwimlane: insertTimeStampsInSetter(
    (
      processId: string,
      swimlaneId: string,
      swimlaneProps: Partial<Swimlane>
    ) => {
      const { megaJSON } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const swimlane = process.swimlanes[swimlaneId];
      if (!swimlane) return;

      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: { ...swimlane, ...swimlaneProps },
          componentKey: ComponentKey.SWIMLANES
        })
      ];
      get().applyChanges(changes);
    },
    true
  ),

  updateRule: insertTimeStampsInSetter(
    (processId: string, ruleId: string, ruleProps: Partial<Rule>) => {
      const { megaJSON } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const rule = process.rules[ruleId];
      if (!rule) return;

      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: { ...rule, ...ruleProps },
          componentKey: ComponentKey.RULES
        })
      ];
      get().applyChanges(changes);
    },
    true
  ),

  updateConditionGroup: insertTimeStampsInSetter(
    (
      processId: string,
      conditionGroupId: string,
      props: Partial<ConditionGroupData>
    ) => {
      const { megaJSON } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const conditionGroup = process.conditionGroups[conditionGroupId];
      if (!conditionGroup) return;

      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: { ...conditionGroup, ...props },
          componentKey: ComponentKey.CONDITION_GROUPS
        })
      ];
      get().applyChanges(changes);
    },
    true
  ),

  updateCondition: insertTimeStampsInSetter(
    (processId: string, conditionId: string, props: Partial<ConditionData>) => {
      const { megaJSON } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const condition = process.conditions[conditionId];
      if (!condition) return;

      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: { ...condition, ...props } as ConditionData,
          componentKey: ComponentKey.CONDITIONS
        })
      ];
      get().applyChanges(changes);
    },
    true
  ),

  updateEventRule: insertTimeStampsInSetter(
    (processId: string, eventRule: EventRule) => {
      const { megaJSON, applyChanges } = get();
      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: eventRule,
          componentKey: ComponentKey.EVENT_RULES
        })
      ];
      applyChanges(changes);
    }
  ),

  updateStateRule: insertTimeStampsInSetter(
    (processId: string, stateRule: StateRule) => {
      const { megaJSON, applyChanges } = get();
      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: stateRule,
          componentKey: ComponentKey.STATE_RULES
        })
      ];
      applyChanges(changes);
    }
  ),

  replaceAllEventRules: (
    processId: string,
    eventRules: Process["eventRules"] = {}
  ) => {
    const { megaJSON, applyChanges } = get();
    const changes = Object.values(eventRules).map(eventRule =>
      mutate.update({
        state: megaJSON,
        processId,
        component: eventRule,
        componentKey: ComponentKey.EVENT_RULES
      })
    );
    const currentEventRules = megaJSON.processes[processId]?.eventRules;
    if (currentEventRules) {
      changes.push(
        ...Object.values(currentEventRules)
          .filter(eventRule => !eventRules[eventRule.__studioId])
          .map(eventRule =>
            mutate.remove({
              state: megaJSON,
              processId,
              component: eventRule,
              componentKey: ComponentKey.EVENT_RULES
            })
          )
      );
    }
    applyChanges(changes);
  },

  replaceAllStateRules: (
    processId: string,
    stateRules: Process["stateRules"] = {}
  ) => {
    const { megaJSON, applyChanges } = get();
    const changes = Object.values(stateRules).map(stateRule =>
      mutate.update({
        state: megaJSON,
        processId,
        component: stateRule,
        componentKey: ComponentKey.STATE_RULES
      })
    );
    const currentStateRules = megaJSON.processes[processId]?.stateRules;
    if (currentStateRules) {
      changes.push(
        ...Object.values(currentStateRules)
          .filter(stateRule => !stateRules[stateRule.__studioId])
          .map(stateRule =>
            mutate.remove({
              state: megaJSON,
              processId,
              component: stateRule,
              componentKey: ComponentKey.STATE_RULES
            })
          )
      );
    }
    applyChanges(changes);
  },

  updateBulkAction: insertTimeStampsInSetter(
    (
      processId: string,
      bulkActionId: string,
      bulkActionProps: Partial<BulkAction>
    ) => {
      const { megaJSON, applyChanges } = get();
      const process = getProcessFromMegaJSON(megaJSON, processId);
      const bulkAction = process.bulkActions[bulkActionId];
      if (!bulkAction) return;

      const changes = [
        mutate.update({
          state: megaJSON,
          processId,
          component: { ...bulkAction, ...bulkActionProps },
          componentKey: ComponentKey.BULK_ACTIONS
        })
      ];
      applyChanges(changes);
    }
  ),

  // Remove functions
  removeField: (processId: string, fieldId: string) => {
    get().removeFields(processId, [fieldId]);
  },

  removeStep: (processId: string, stepId: string) => {
    get().removeSteps(processId, [stepId]);
  },

  removeSwimlane: (processId, swimlaneId) => {
    get().removeSwimlanes(processId, [swimlaneId]);
  },

  // Delete (private) functions
  __deleteField: (processId, fieldId) => {
    const { megaJSON, applyChanges } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    const field = process.fields[fieldId];
    if (!field) return;

    const changes = [
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.FIELDS,
        component: field
      })
    ];
    applyChanges(changes);
  },

  __deleteStep: (processId, stepId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    const step = process.steps[stepId];
    if (!step) return;

    const changes = [
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.STEPS,
        component: step
      })
    ];
    get().applyChanges(changes);
  },

  __deleteSwimlane: (processId, swimlaneId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    const swimlane = process.swimlanes[swimlaneId];
    if (!swimlane) return;

    const changes = [
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.SWIMLANES,
        component: swimlane
      })
    ];
    get().applyChanges(changes);
  },

  removeEventRule: (processId, eventRuleId) => {
    const { megaJSON, applyChanges } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    if (!process.eventRules?.[eventRuleId]) {
      throw new Error(`Event rule not found: ${eventRuleId}`);
    }

    const changes = [
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.EVENT_RULES,
        component: process?.eventRules[eventRuleId]
      })
    ];

    applyChanges(changes);
  },

  removeStateRule: (processId, stateRuleId) => {
    const { megaJSON, applyChanges } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    if (!process.stateRules?.[stateRuleId]) {
      throw new Error(`State rule not found: ${stateRuleId}`);
    }

    const changes = [
      mutate.remove({
        state: megaJSON,
        processId,
        componentKey: ComponentKey.STATE_RULES,
        component: process?.stateRules[stateRuleId]
      })
    ];

    applyChanges(changes);
  },

  // Bulk remove functions
  removeFields: (processId, fieldIds) => {
    get().transaction(() => {
      const fields = get().getFields(processId, fieldIds);

      // Getting all ruleIds of rules that are applied on the fields
      const ruleIdsToRemove: string[] = [];
      fields.forEach(field => {
        if (!field) return;

        ComponentTypeToRuleTypeMap[ComponentKey.FIELDS].forEach(ruleType => {
          if (
            field[ruleType as keyof Field] &&
            Array.isArray(field[ruleType as keyof Field]) // To skip raw ruleTypes
          ) {
            ruleIdsToRemove.push(...(field[ruleType as keyof Field] as string));
          }
        });
      });

      get().removeRules(processId, ruleIdsToRemove);

      fieldIds.forEach(fieldId => {
        get().__deleteField(processId, fieldId);
      });
    });
  },

  removeSteps: (processId, stepIds) => {
    get().transaction(() => {
      const steps = get().getSteps(processId, stepIds);

      // Getting all ruleIds of rules that are applied on the steps
      // AND fields of these steps and removing them
      const ruleIdsToRemove: string[] = [];
      const fieldIdsToRemove: string[] = [];

      steps.forEach(step => {
        if (!step) return;

        fieldIdsToRemove.push(...step.fields);

        ComponentTypeToRuleTypeMap[ComponentKey.STEPS].forEach(ruleType => {
          if (step[ruleType as keyof Step]) {
            ruleIdsToRemove.push(...(step[ruleType as keyof Step] as string));
          }
        });
      });

      get().removeRules(processId, ruleIdsToRemove);
      get().removeFields(processId, fieldIdsToRemove);

      stepIds.forEach(stepId => {
        get().__deleteStep(processId, stepId);
      });
    });
  },

  removeSwimlanes: (processId, swimlaneIds) => {
    get().transaction(() => {
      const swimlanes = get().getSwimlanes(processId, swimlaneIds);

      // Getting all ruleIds of rules that are applied on the swimlanes
      // AND steps of these swimlanes and removing them
      const ruleIdsToRemove: string[] = [];
      const stepIdsToRemove: string[] = [];

      swimlanes.forEach(swimlane => {
        if (!swimlane) return;

        stepIdsToRemove.push(...swimlane.steps);

        ComponentTypeToRuleTypeMap[ComponentKey.SWIMLANES].forEach(ruleType => {
          if (swimlane[ruleType as keyof Swimlane]) {
            ruleIdsToRemove.push(
              ...(swimlane[ruleType as keyof Swimlane] as string)
            );
          }
        });
      });

      get().removeRules(processId, ruleIdsToRemove);
      get().removeSteps(processId, stepIdsToRemove);

      swimlaneIds.forEach(swimlaneId => {
        get().__deleteSwimlane(processId, swimlaneId);
      });
    });
  },

  removeRule: (processId, ruleId) => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);
    const rule = process.rules[ruleId];
    if (!rule) return;

    const changes: Change[] = createRemoveRulesChanges({
      processId,
      megaJSON,
      ruleId
    });

    if (!changes.length) return;
    get().applyChanges(changes);
  },

  removeRules: (processId, ruleIds) => {
    get().transaction(() => {
      ruleIds.forEach(ruleId => {
        get().removeRule(processId, ruleId);
      });
    });
  },

  deleteProcess: processId => {
    const { megaJSON } = get();
    const process = getProcessFromMegaJSON(megaJSON, processId);

    if (!process) return;
    const {
      swimlanes,
      steps,
      fields,
      rules,
      conditions,
      conditionGroups,
      bulkActions,
      eventRules,
      stateRules
    } = process;

    const changes: Change[] = [
      mutate.updateProcess({
        state: megaJSON,
        processId,
        process: {
          ...process.process,
          swimlaneSequence: []
        }
      }),
      ...Object.values(swimlanes).map(swimlane =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.SWIMLANES,
          component: swimlane
        })
      ),
      ...Object.values(steps).map(step =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.STEPS,
          component: step
        })
      ),
      ...Object.values(fields).map(field =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.FIELDS,
          component: field
        })
      ),
      ...Object.values(rules).map(rule =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.RULES,
          component: rule
        })
      ),
      ...Object.values(conditions).map(condition =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.CONDITIONS,
          component: condition
        })
      ),
      ...Object.values(conditionGroups).map(conditionGroup =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.CONDITION_GROUPS,
          component: conditionGroup
        })
      ),
      ...Object.values(bulkActions).map(bulkAction =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.BULK_ACTIONS,
          component: bulkAction
        })
      ),
      ...Object.values(eventRules || {}).map(eventRule =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.EVENT_RULES,
          component: eventRule
        })
      ),
      ...Object.values(stateRules || {}).map(stateRule =>
        mutate.remove({
          state: megaJSON,
          processId,
          componentKey: ComponentKey.STATE_RULES,
          component: stateRule
        })
      )
    ];

    get().applyChanges(changes);
  }
});
